import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import {
  CancelInvoiceNfs,
  CancellationCode,
  ConsultInvoiceNfs,
  invoiceNFSdownloadAllPDF,
  MailInvoiceNfs,
  PdfInvoiceNfs,
  PdfPreviewInvoiceNfs,
  TransmitInvoiceNfs,
  XmlInvoiceNfs,
  XmlPreviewInvoiceNfs,
} from './NfseController.services';
import { useMutation, useQuery } from 'react-query';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { AxiosError } from 'axios';
import arrayBufferParse from '@/utils/functions/arrayBufferParse';

const INVOICE_LIST_QUERY = 'invoices';
const INVOICE_DETAIL_QUERY = 'invoice';
const INVOICE_CANCELLATION_CODE = 'cancelationCode';

export function useCancellationCode(invoiceId: number) {
  return useQuery(
    [INVOICE_CANCELLATION_CODE],
    () => CancellationCode(invoiceId),
    { enabled: !!invoiceId },
  );
}

export function useTransmitInvoiceNfs() {
  const queryChanel = useQueryChanel();

  return useMutation(TransmitInvoiceNfs, {
    async onSuccess(_, { invoiceId }) {
      queryChanel({
        invalidateQueries: [
          [INVOICE_DETAIL_QUERY, invoiceId],
          [INVOICE_LIST_QUERY],
          ['invoiceDetail', invoiceId],
        ],
        refetchQueries: [
          [INVOICE_DETAIL_QUERY, invoiceId],
          [INVOICE_LIST_QUERY],
          ['invoiceDetail', invoiceId],
        ],
      });

      notification.success({
        message: 'Sucesso',
        description: `NFSe transmitida com sucesso`,
      });
    },
    onError(_, { invoiceId }) {
      queryChanel({
        refetchQueries: [[INVOICE_DETAIL_QUERY, invoiceId]],
      });
    },
  });
}

export function useCancelInvoiceNfs() {
  const queryChanel = useQueryChanel();

  return useMutation(CancelInvoiceNfs, {
    async onSuccess(_, { invoiceId }) {
      queryChanel({
        invalidateQueries: [
          [INVOICE_DETAIL_QUERY, invoiceId],
          [INVOICE_LIST_QUERY],
        ],
        refetchQueries: [
          [INVOICE_DETAIL_QUERY, invoiceId],
          [INVOICE_LIST_QUERY],
        ],
      });

      notification.success({
        message: 'Sucesso',
        description: 'NFS-e cancelada com sucesso',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useConsultInvoiceNfs() {
  const queryChanel = useQueryChanel();

  return useMutation(ConsultInvoiceNfs, {
    async onSuccess(message, invoiceId) {
      if (message) notification.success({ message });

      queryChanel({
        invalidateQueries: [
          [INVOICE_DETAIL_QUERY, invoiceId],
          [INVOICE_LIST_QUERY],
          ['invoiceDetail', invoiceId],
        ],
        refetchQueries: [
          [INVOICE_DETAIL_QUERY, invoiceId],
          [INVOICE_LIST_QUERY],
          ['invoiceDetail', invoiceId],
        ],
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useMailInvoiceNfs() {
  return useMutation(MailInvoiceNfs, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: `NFS-e enviada para o e-mail do destinatário.`,
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function usePdfInvoiceNfs() {
  return useMutation(PdfInvoiceNfs, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'PDF da Nota Fiscal Gerado com Sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useXmlInvoiceNfs() {
  return useMutation(XmlInvoiceNfs, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description: 'XML da Nota Fiscal gerado com sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function usePdfPreviewInvoiceNfs() {
  return useMutation(PdfPreviewInvoiceNfs, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description:
          'Pré-visualização do PDF da Nota Fiscal gerada com sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useXmlPreviewInvoiceNfs() {
  return useMutation(XmlPreviewInvoiceNfs, {
    async onSuccess() {
      notification.success({
        message: 'Sucesso',
        description:
          'Pré-visualização do XML da Nota Fiscal gerada com sucesso!',
      });
    },

    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceNFSAllPDF() {
  return useMutation({
    mutationFn: invoiceNFSdownloadAllPDF,
    onSuccess() {
      notification.success({
        message: 'Arquivo zip dos PDFs baixado com sucesso!',
      });
    },
    onError(error) {
      const err = error as AxiosError<ArrayBufferLike>;

      if (err.response?.data) {
        interface ResponseError {
          code: string;
          message: string;
          timestamp: string;
        }

        const response = arrayBufferParse<ResponseError>(err.response.data);

        return notification.error({
          message: 'Download dos PDFs',
          description: response?.message,
        });
      }

      notificationError(error);
    },
  });
}
